import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Chip,
  CircularProgress,
  IconButton,
  Card,
  CardContent,
  Grid,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import {
  Assessment as ValidateIcon,
  Close as CloseIcon,
  FolderOpen as FolderIcon,
  CheckCircleOutline as CheckIcon,
  Description as FileIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { resumeService } from '../services/resumeService';

interface Resume {
  id: string;
  candidateName: string;
  jobId?: string;
  filePath?: string;
  fileName?: string;
  resume_location?: string;
  _original?: any;
}

interface ValidationDimension {
  id: string;
  label: string;
  description: string;
}

interface ResumeValidationDialogProps {
  open: boolean;
  onClose: () => void;
  selectedResumes: Resume[];
  onValidate: (resumeIds: string[], dimensions: string[]) => void;
  loading?: boolean;
}

interface ParsedResumeResult {
  resumeId: string;
  candidateName: string;
  success: boolean;
  data?: any;
  matchData?: any;
  error?: string;
}

// Validation dimensions that can be selected
const VALIDATION_DIMENSIONS: ValidationDimension[] = [
  {
    id: 'experience_relevance',
    label: 'Experience Relevance & Role Alignment',
    description: 'Evaluate if candidate experience aligns with job requirements',
  },
  {
    id: 'skills_match',
    label: 'Skills & Technical Requirements Match',
    description: 'Assess technical skills and competencies against job needs',
  },
  {
    id: 'achievement_relevance',
    label: 'Achievement Relevance & Impact Alignment',
    description: 'Analyze accomplishments and their relevance to the role',
  },
  {
    id: 'education_qualification',
    label: 'Education & Qualification Alignment',
    description: 'Verify educational background meets job criteria',
  },
  {
    id: 'communication_quality',
    label: 'Communication & Presentation Quality',
    description: 'Evaluate clarity, professionalism, and presentation of resume',
  },
  {
    id: 'career_progression',
    label: 'Career Progression & Growth Pattern',
    description: 'Analyze career trajectory and professional growth',
  },
  {
    id: 'domain_expertise',
    label: 'Domain Knowledge & Industry Expertise',
    description: 'Assess industry-specific knowledge and domain expertise',
  },
  {
    id: 'certifications_training',
    label: 'Certifications & Professional Training',
    description: 'Evaluate relevant certifications and continuous learning',
  },
];

const ResumeValidationDialog: React.FC<ResumeValidationDialogProps> = ({
  open,
  onClose,
  selectedResumes,
  onValidate,
  loading = false,
}) => {
  const [selectedDimensions, setSelectedDimensions] = useState<string[]>([]);
  const [validating, setValidating] = useState(false);
  const [validationResults, setValidationResults] = useState<ParsedResumeResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [currentProgress, setCurrentProgress] = useState({ current: 0, total: 0, status: '', candidateName: '' });

  // Reset state when dialog opens - start with nothing selected
  useEffect(() => {
    if (open) {
      setSelectedDimensions([]);
      setValidationResults([]);
      setShowResults(false);
      setCurrentProgress({ current: 0, total: 0, status: '', candidateName: '' });
    }
  }, [open]);

  const handleDimensionToggle = (dimensionId: string) => {
    setSelectedDimensions((prev) => {
      if (prev.includes(dimensionId)) {
        return prev.filter((id) => id !== dimensionId);
      } else {
        return [...prev, dimensionId];
      }
    });
  };

  const handleSelectAllToggle = () => {
    if (selectedDimensions.length === VALIDATION_DIMENSIONS.length) {
      setSelectedDimensions([]);
    } else {
      setSelectedDimensions(VALIDATION_DIMENSIONS.map((d) => d.id));
    }
  };

  const handleValidate = async () => {
    if (selectedDimensions.length === 0) {
      return;
    }
    
    setValidating(true);
    setShowResults(false);
    setCurrentProgress({ current: 0, total: selectedResumes.length, status: 'Starting validation...', candidateName: '' });
    
    try {
      const results: ParsedResumeResult[] = [];
      
      // Process each resume
      for (let i = 0; i < selectedResumes.length; i++) {
        const resume = selectedResumes[i];
        const resumePath = getResumePath(resume);
        
        try {
          // Update progress - Parsing
          setCurrentProgress({ 
            current: i + 1, 
            total: selectedResumes.length, 
            status: 'Parsing resume from storage...', 
            candidateName: resume.candidateName 
          });
          
          console.log(`Parsing resume for ${resume.candidateName}: ${resumePath}`);
          
          // Step 1: Call the parse API
          const parsedData = await resumeService.parseResumeData(resumePath);
          console.log(`Successfully parsed resume for ${resume.candidateName}`);
          
          // Update progress - Matching
          setCurrentProgress({ 
            current: i + 1, 
            total: selectedResumes.length, 
            status: 'Analyzing resume against job requirements...', 
            candidateName: resume.candidateName 
          });
          
          // Step 2: Call the match API with parsed data
          let matchData = null;
          try {
            // Get job_id from either camelCase or snake_case format
            const jobId = resume.jobId || resume._original?.job_id || '';
            console.log(`Matching resume for ${resume.candidateName} against job ${jobId}`);
            
            if (jobId) {
              matchData = await resumeService.matchResume(
                selectedDimensions,
                parsedData,
                jobId
              );
              console.log(`Successfully matched resume for ${resume.candidateName}`);
              console.log('Match data structure:', JSON.stringify(matchData, null, 2));
              
              // Update progress - Completed
              setCurrentProgress({ 
                current: i + 1, 
                total: selectedResumes.length, 
                status: 'Match analysis completed!', 
                candidateName: resume.candidateName 
              });
            } else {
              console.warn(`No job ID found for resume ${resume.candidateName}`);
              matchData = { error: 'No job ID found for matching' };
            }
          } catch (matchError) {
            console.error(`Failed to match resume for ${resume.candidateName}:`, matchError);
            matchData = { error: matchError instanceof Error ? matchError.message : 'Failed to match resume' };
          }
          
          results.push({
            resumeId: resume.id,
            candidateName: resume.candidateName,
            success: true,
            data: parsedData,
            matchData: matchData,
          });
        } catch (error) {
          console.error(`Failed to parse resume for ${resume.candidateName}:`, error);
          
          setCurrentProgress({ 
            current: i + 1, 
            total: selectedResumes.length, 
            status: 'Failed to process resume', 
            candidateName: resume.candidateName 
          });
          
          results.push({
            resumeId: resume.id,
            candidateName: resume.candidateName,
            success: false,
            error: error instanceof Error ? error.message : 'Failed to parse resume',
          });
        }
      }
      
      setValidationResults(results);
      setShowResults(true);
      setCurrentProgress({ current: selectedResumes.length, total: selectedResumes.length, status: 'All validations completed!', candidateName: '' });
      
    } catch (error) {
      console.error('Validation error:', error);
    } finally {
      setValidating(false);
    }
  };

  const handleClose = () => {
    if (!loading && !validating) {
      onClose();
    }
  };

  const handleFinish = () => {
    // Call the original onValidate to update the parent component
    const resumeIds = selectedResumes.map((r) => r.id);
    onValidate(resumeIds, selectedDimensions);
  };

  const getResumePath = (resume: Resume) => {
    return resume._original?.resume_location || 
           resume.resume_location || 
           resume.filePath || 
           resume.fileName || 
           'Path not available';
  };

  // Helper function to format field names
  const formatFieldName = (key: string): string => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Helper to map dimension id to label
  const getDimensionLabel = (id: string): string => {
    return VALIDATION_DIMENSIONS.find(d => d.id === id)?.label || formatFieldName(id);
  };

  // If the object is a wrapper with a single nested key (e.g., { education_assessment: { ... } }), unwrap it
  const unwrapSingleKeyObject = (obj: any): any => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
    const keys = Object.keys(obj);
    if (keys.length === 1 && typeof obj[keys[0]] === 'object') {
      return obj[keys[0]];
    }
    return obj;
  };

  // Render a simple list of strings
  const renderStringList = (title: string, items: string[], styles?: any) => {
    if (!items || items.length === 0) return null;
    return (
      <Card elevation={0} sx={{ mb: 2, ...(styles || {}) }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.5px', color: '#334155' }}>
            {title}
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2, mb: 0 }}>
            {items.map((item, idx) => (
              <Box component="li" key={idx} sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ color: '#1e293b', lineHeight: 1.6 }}>{item}</Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Render a set of chips for tags
  const renderChips = (title: string, items: string[], color: string = '#667eea') => {
    if (!items || items.length === 0) return null;
    return (
      <Card elevation={0} sx={{ mb: 2, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.5px', color: '#334155', display: 'block', mb: 1 }}>
            {title}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {items.map((item, idx) => (
              <Chip key={idx} label={item} size="small" sx={{ backgroundColor: 'white', border: `1px solid ${color}`, color: color, fontWeight: 500 }} />
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Render key-value object as list (e.g., proficiency levels)
  const renderKeyValueList = (title: string, obj: Record<string, any>) => {
    if (!obj || typeof obj !== 'object') return null;
    const entries = Object.entries(obj);
    if (entries.length === 0) return null;
    return (
      <Card elevation={0} sx={{ mb: 2, backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.5px', color: '#1e40af', display: 'block', mb: 1 }}>
            {title}
          </Typography>
          <Grid container spacing={1}>
            {entries.map(([k, v]) => (
              <Grid item xs={12} md={6} key={k}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, borderBottom: '1px dotted #bfdbfe', py: 0.5 }}>
                  <Typography variant="body2" sx={{ color: '#1e293b' }}>{k}</Typography>
                  <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 600 }}>{String(v)}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  // Render common fields for an evaluation.result object
  const renderEvaluationResult = (resultObj: any) => {
    if (!resultObj || typeof resultObj !== 'object') return null;

    // Some dimensions nest details under "assessment" or under a single-key wrapper
    let root = resultObj.assessment || resultObj;
    root = unwrapSingleKeyObject(root);

    return (
      <Box>
        {root.summary && (
          <Card elevation={0} sx={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.5px' }}>Summary</Typography>
              <Typography variant="body2" sx={{ mt: 1, color: '#1e293b', lineHeight: 1.7 }}>{root.summary}</Typography>
            </CardContent>
          </Card>
        )}

        {typeof root.years_of_experience === 'number' && (
          <Card elevation={0} sx={{ mb: 2, backgroundColor: '#ecfeff', border: '1px solid #a5f3fc' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: '#0e7490' }}>Years of Experience</Typography>
              <Typography variant="body2" sx={{ mt: 1, fontWeight: 600, color: '#0f172a' }}>{root.years_of_experience}</Typography>
            </CardContent>
          </Card>
        )}

        {renderChips('Relevant Industries', root.relevant_industries)}

        {Array.isArray(root.similar_roles) && root.similar_roles.length > 0 && (
          <Card elevation={0} sx={{ mb: 2, backgroundColor: '#faf5ff', border: '1px solid #e9d5ff' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: '#6b21a8', fontSize: '0.65rem', letterSpacing: '0.5px', mb: 1, display: 'block' }}>Similar Roles</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {root.similar_roles.map((r: any, idx: number) => (
                  <Box key={idx} sx={{ p: 1.25, border: '1px solid #e5e7eb', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1f2937' }}>{r.title}{r.company ? ` • ${r.company}` : ''}</Typography>
                    {r.duration && (
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>{r.duration}</Typography>
                    )}
                    {r.relevance && (
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>{r.relevance}</Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        )}

        {root.progression_in_responsibility && (
          <Card elevation={0} sx={{ mb: 2, backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: '#334155' }}>Progression in Responsibility</Typography>
              <Typography variant="body2" sx={{ mt: 1, color: '#0f172a' }}>{root.progression_in_responsibility}</Typography>
            </CardContent>
          </Card>
        )}

        {typeof root.score === 'number' && (
          <Card elevation={0} sx={{ mb: 2, backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: '#166534' }}>Score</Typography>
              <Chip label={root.score} size="small" sx={{ ml: 1, fontWeight: 700, backgroundColor: 'white', border: '1px solid #86efac', color: '#166534' }} />
              {root.score_explanation && (
                <Typography variant="body2" sx={{ mt: 1, color: '#14532d' }}>{root.score_explanation}</Typography>
              )}
            </CardContent>
          </Card>
        )}

        {renderStringList('Specific Examples', root.specific_examples)}
        {renderChips('Matched Skills', root.matched_skills, '#2563eb')}
        {renderChips('Missing Skills', root.missing_skills, '#dc2626')}
        {root.detailed_assessment && (
          <Card elevation={0} sx={{ mb: 2, backgroundColor: '#fff7ed', border: '1px solid #fed7aa' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: '#9a3412' }}>Detailed Assessment</Typography>
              <Typography variant="body2" sx={{ mt: 1, color: '#7c2d12' }}>{root.detailed_assessment}</Typography>
            </CardContent>
          </Card>
        )}
        {renderKeyValueList('Proficiency Levels', root.proficiency_levels)}
        {renderChips('Certifications', root.certifications, '#1d4ed8')}
        {renderStringList('Key Achievements', root.key_achievements, { backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' })}
        {renderStringList('Relevance to Job Requirements', root.relevance_to_job_requirements, { backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' })}
        {renderStringList('Quantifiable Results', root.quantifiable_results, { backgroundColor: '#ecfeff', border: '1px solid #a5f3fc' })}
        {root.scope_of_achievements && (
          <Card elevation={0} sx={{ mb: 2, backgroundColor: '#f5f3ff', border: '1px solid #ddd6fe' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: '#4c1d95' }}>Scope of Achievements</Typography>
              <Typography variant="body2" sx={{ mt: 1, color: '#3730a3' }}>{root.scope_of_achievements}</Typography>
            </CardContent>
          </Card>
        )}
        {renderStringList('Improvement Suggestions', root.improvement_suggestions, { backgroundColor: '#fff7ed', border: '1px solid #fed7aa' })}
      </Box>
    );
  };

  // Generic deep renderer to ensure nothing is omitted
  const renderDeepNode = (key: string, value: any, depth: number = 0): React.ReactNode => {
    const label = formatFieldName(key);

    if (value === null || value === undefined) {
      return (
        <Box key={`${key}-${depth}`} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
          <Typography variant="body2" sx={{ color: '#334155' }}>{label}</Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>—</Typography>
        </Box>
      );
    }

    if (typeof value !== 'object') {
      return (
        <Box key={`${key}-${depth}`} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
          <Typography variant="body2" sx={{ color: '#334155' }}>{label}</Typography>
          <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 600 }}>{String(value)}</Typography>
        </Box>
      );
    }

    if (Array.isArray(value)) {
      return (
        <Card key={`${key}-${depth}`} elevation={0} sx={{ mb: 1.25, backgroundColor: '#ffffff', border: '1px solid #e2e8f0' }}>
          <CardContent sx={{ p: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>{label}</Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2, mb: 0 }}>
              {value.map((item, idx) => (
                <Box component="li" key={`${key}-${idx}`} sx={{ mb: 0.75 }}>
                  {typeof item === 'object' ? (
                    <Box sx={{ p: 1, border: '1px dashed #e2e8f0', borderRadius: 1 }}>
                      {Object.entries(item).map(([k, v]) => renderDeepNode(k, v, depth + 1))}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#0f172a' }}>{String(item)}</Typography>
                  )}
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      );
    }

    // Object
    return (
      <Card key={`${key}-${depth}`} elevation={0} sx={{ mb: 1.25, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
        <CardContent sx={{ p: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>{label}</Typography>
          <Box sx={{ mt: 1 }}>
            {Object.entries(value).map(([k, v]) => (
              <Box key={`${key}-${k}-${depth}`} sx={{ mb: 0.5 }}>
                {renderDeepNode(k, v, depth + 1)}
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Helper function to render match analysis section
  const renderMatchSection = (sectionKey: string, sectionData: any) => {
    if (!sectionData || typeof sectionData !== 'object') {
      return (
        <Typography variant="body2" sx={{ color: '#1e293b' }}>
          {String(sectionData)}
        </Typography>
      );
    }

    return (
      <Box>
        {/* Summary */}
        {sectionData.summary && (
          <Card elevation={0} sx={{ 
            backgroundColor: '#f8fafc', 
            border: '1px solid #e2e8f0',
            mb: 2
          }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ 
                fontWeight: 700, 
                color: '#64748b',
                textTransform: 'uppercase',
                fontSize: '0.65rem',
                letterSpacing: '0.5px'
              }}>
                Summary
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: '#1e293b', lineHeight: 1.7 }}>
                {sectionData.summary}
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Key Strengths */}
        {sectionData.key_strengths && Array.isArray(sectionData.key_strengths) && sectionData.key_strengths.length > 0 && (
          <Card elevation={0} sx={{ 
            backgroundColor: '#f0fdf4', 
            border: '1px solid #bbf7d0',
            mb: 2
          }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ 
                fontWeight: 700, 
                color: '#15803d',
                textTransform: 'uppercase',
                fontSize: '0.65rem',
                letterSpacing: '0.5px',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}>
                ✓ Key Strengths
              </Typography>
              <Box component="ul" sx={{ mt: 1, pl: 2, mb: 0 }}>
                {sectionData.key_strengths.map((strength: string, idx: number) => (
                  <Box component="li" key={idx} sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ color: '#1e293b', lineHeight: 1.6 }}>
                      {strength}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Potential Gaps */}
        {sectionData.potential_gaps && Array.isArray(sectionData.potential_gaps) && sectionData.potential_gaps.length > 0 && (
          <Card elevation={0} sx={{ 
            backgroundColor: '#fef3c7', 
            border: '1px solid #fde68a',
            mb: 2
          }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ 
                fontWeight: 700, 
                color: '#92400e',
                textTransform: 'uppercase',
                fontSize: '0.65rem',
                letterSpacing: '0.5px',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}>
                ⚠ Potential Gaps
              </Typography>
              <Box component="ul" sx={{ mt: 1, pl: 2, mb: 0 }}>
                {sectionData.potential_gaps.map((gap: string, idx: number) => (
                  <Box component="li" key={idx} sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ color: '#1e293b', lineHeight: 1.6 }}>
                      {gap}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Fit Assessment */}
        {sectionData.fit_assessment && (
          <Card elevation={0} sx={{ 
            backgroundColor: '#eff6ff', 
            border: '1px solid #bfdbfe',
            mb: 2
          }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ 
                fontWeight: 700, 
                color: '#1e40af',
                textTransform: 'uppercase',
                fontSize: '0.65rem',
                letterSpacing: '0.5px'
              }}>
                Fit Assessment
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: '#1e293b', lineHeight: 1.7 }}>
                {sectionData.fit_assessment}
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        }
      }}
    >
      {/* Modern Header */}
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        pb: 3,
        position: 'relative',
      }}>
        <IconButton
          onClick={handleClose}
          disabled={loading}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'white',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <CloseIcon />
        </IconButton>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pr: 5 }}>
          <Box 
            sx={{ 
              backgroundColor: 'rgba(255,255,255,0.2)', 
              borderRadius: 2, 
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ValidateIcon sx={{ fontSize: 32 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              Resume Validation
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.95 }}>
              Select validation dimensions for {selectedResumes.length} resume{selectedResumes.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {validating && !showResults ? (
          // Progress View
          <Box sx={{ py: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <CircularProgress size={60} sx={{ color: '#667eea' }} />
              <Typography variant="h6" sx={{ mt: 3, fontWeight: 600, color: '#1e293b' }}>
                Processing Resumes...
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {currentProgress.current} of {currentProgress.total} completed
              </Typography>
            </Box>

            <Card elevation={0} sx={{ 
              border: '2px solid #667eea',
              borderRadius: 2,
              backgroundColor: '#f5f7ff',
              maxWidth: 600,
              mx: 'auto'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box sx={{ 
                    width: 40, 
                    height: 40, 
                    borderRadius: '50%', 
                    backgroundColor: '#667eea',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 600
                  }}>
                    {currentProgress.current}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    {currentProgress.candidateName && (
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                        {currentProgress.candidateName}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ color: '#667eea', fontWeight: 500 }}>
                      {currentProgress.status}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        ) : !showResults ? (
        <>
          <Grid container spacing={3}>
          {/* Left Column - Selected Resumes */}
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <FolderIcon sx={{ color: '#667eea', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                Selected Resumes
              </Typography>
              <Chip 
                label={selectedResumes.length} 
                size="small" 
                sx={{ 
                  backgroundColor: '#667eea',
                  color: 'white',
                  fontWeight: 600,
                  height: 20,
                  fontSize: '0.75rem',
                }} 
              />
            </Box>
            
            <Box sx={{ 
              maxHeight: 320, 
              overflowY: 'auto',
              pr: 1,
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-track': { 
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': { 
                backgroundColor: '#cbd5e0',
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: '#94a3b8',
                }
              },
            }}>
              {selectedResumes.map((resume, index) => (
                <Card
                  key={resume.id}
                  elevation={0}
                  sx={{
                    mb: 1,
                    border: '1px solid #e2e8f0',
                    borderRadius: 1.5,
                    transition: 'all 0.15s',
                    '&:hover': {
                      borderColor: '#667eea',
                      boxShadow: '0 2px 4px rgba(102, 126, 234, 0.1)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <FileIcon sx={{ color: '#667eea', fontSize: 16, mt: 0.2, flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 600,
                            mb: 0.5,
                            color: '#1e293b',
                            fontSize: '0.875rem',
                          }}
                        >
                          {resume.candidateName}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            display: 'block',
                            color: '#64748b',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontFamily: 'monospace',
                            fontSize: '0.65rem',
                            backgroundColor: '#f8fafc',
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 0.5,
                          }}
                        >
                          {getResumePath(resume)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Grid>

          {/* Right Column - Validation Dimensions */}
          <Grid item xs={12} md={8}>
            <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                Validation Dimensions
              </Typography>
              <Button
                size="small"
                onClick={handleSelectAllToggle}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  color: '#667eea',
                  py: 0.5,
                  px: 1.5,
                }}
              >
                {selectedDimensions.length === VALIDATION_DIMENSIONS.length ? 'Deselect All' : 'Select All'}
              </Button>
            </Box>

            <Box sx={{ 
              maxHeight: 320,
              overflowY: 'auto',
              pr: 1,
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-track': { 
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': { 
                backgroundColor: '#cbd5e0',
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: '#94a3b8',
                }
              },
            }}>
              {VALIDATION_DIMENSIONS.map((dimension) => {
                const isSelected = selectedDimensions.includes(dimension.id);
                return (
                  <Box
                    key={dimension.id}
                    sx={{
                      mb: 1,
                      p: 1.5,
                      border: isSelected ? '2px solid #667eea' : '1px solid #e2e8f0',
                      borderRadius: 1.5,
                      backgroundColor: isSelected ? '#f5f7ff' : 'white',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        borderColor: '#667eea',
                        backgroundColor: isSelected ? '#f5f7ff' : '#fafbff',
                      },
                    }}
                    onClick={() => handleDimensionToggle(dimension.id)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleDimensionToggle(dimension.id)}
                        sx={{
                          p: 0,
                          color: '#cbd5e0',
                          '&.Mui-checked': {
                            color: '#667eea',
                          },
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 600,
                            mb: 0.25,
                            color: isSelected ? '#667eea' : '#1e293b',
                            fontSize: '0.875rem',
                            lineHeight: 1.3,
                          }}
                        >
                          {dimension.label}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: '#64748b',
                            lineHeight: 1.3,
                            fontSize: '0.75rem',
                          }}
                        >
                          {dimension.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Grid>
        </Grid>

        {/* Summary Footer */}
        {selectedDimensions.length > 0 && (
          <Card
            elevation={0}
            sx={{
              mt: 3,
              background: 'linear-gradient(135deg, #e0f2fe 0%, #ddd6fe 100%)',
              border: 'none',
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CheckIcon sx={{ color: '#667eea', fontSize: 22 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  {selectedDimensions.length} dimension{selectedDimensions.length !== 1 ? 's' : ''} selected
                </Typography>
                <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {selectedDimensions.slice(0, 3).map((dimId) => {
                    const dim = VALIDATION_DIMENSIONS.find(d => d.id === dimId);
                    return (
                      <Chip 
                        key={dimId}
                        label={dim?.label.split('&')[0].trim()}
                        size="small"
                        sx={{
                          backgroundColor: 'white',
                          border: '1px solid #667eea',
                          color: '#667eea',
                          fontWeight: 500,
                          fontSize: '0.7rem',
                          height: 22,
                        }}
                      />
                    );
                  })}
                  {selectedDimensions.length > 3 && (
                    <Chip 
                      label={`+${selectedDimensions.length - 3} more`}
                      size="small"
                      sx={{
                        backgroundColor: '#667eea',
                        color: 'white',
                        fontWeight: 500,
                        fontSize: '0.7rem',
                        height: 22,
                      }}
                    />
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}
        </>
        ) : (
          // Results View
          <Box>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CheckIcon sx={{ color: '#667eea', fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Match Analysis Results
              </Typography>
              <Chip 
                label={`${validationResults.filter(r => r.success && r.matchData).length}/${validationResults.length} Analyzed`}
                size="small"
                sx={{
                  backgroundColor: '#dcfce7',
                  color: '#15803d',
                  fontWeight: 600,
                }}
              />
            </Box>

            <Box sx={{ maxHeight: 450, overflowY: 'auto' }}>
              {validationResults.map((result, index) => (
                <Accordion 
                  key={result.resumeId}
                  defaultExpanded={index === 0}
                  sx={{
                    mb: 1.5,
                    border: result.success ? '1px solid #d1fae5' : '1px solid #fee2e2',
                    borderRadius: '8px !important',
                    '&:before': { display: 'none' },
                    boxShadow: 'none',
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: result.success ? '#f0fdf4' : '#fef2f2',
                      borderRadius: '8px',
                      '&.Mui-expanded': {
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      {result.success ? (
                        <SuccessIcon sx={{ color: '#15803d', fontSize: 24 }} />
                      ) : (
                        <ErrorIcon sx={{ color: '#dc2626', fontSize: 24 }} />
                      )}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {result.candidateName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {result.success 
                            ? (result.matchData ? 'Match analysis completed' : 'Analysis in progress') 
                            : 'Analysis failed'}
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 3, backgroundColor: '#fafafa' }}>
                    {(() => {
                      console.log('Rendering result for:', result.candidateName);
                      console.log('Result data:', result);
                      console.log('Match data:', result.matchData);
                      return null;
                    })()}
                    {result.success && result.matchData ? (
                      <Box>
                        {/* Display Match Results Only */}
                        {result.matchData.error ? (
                          <Alert severity="warning" sx={{ mt: 1 }}>
                            {result.matchData.error}
                          </Alert>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* New structure handling: match_data.evaluations + summary */}
                            {(() => {
                              const md = result.matchData;
                              // Prefer new structure
                              if (md && md.evaluations && typeof md.evaluations === 'object') {
                                const evalEntries = Object.entries(md.evaluations);
                                return (
                                  <Box>
                                    {/* Overall summary if present */}
                                    {md.summary && (
                                      <Card elevation={0} sx={{ mb: 2, backgroundColor: '#f0f9ff', border: '1px solid #bae6fd' }}>
                                        <CardContent sx={{ p: 2 }}>
                                          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: '#075985' }}>Overall Summary</Typography>
                                          <Grid container spacing={1} sx={{ mt: 1 }}>
                                            <Grid item xs={12} md={4}><Typography variant="body2">Total Criteria: <strong>{md.summary.total_criteria}</strong></Typography></Grid>
                                            <Grid item xs={12} md={4}><Typography variant="body2" sx={{ color: '#166534' }}>Successful: <strong>{md.summary.successful_evaluations}</strong></Typography></Grid>
                                            <Grid item xs={12} md={4}><Typography variant="body2" sx={{ color: '#b91c1c' }}>Failed: <strong>{md.summary.failed_evaluations}</strong></Typography></Grid>
                                          </Grid>
                                        </CardContent>
                                      </Card>
                                    )}

                                    {/* Each evaluation block */}
                                    {evalEntries.map(([dimId, ev]: [string, any], idx: number) => {
                                      if (!ev) return null;
                                      const title = getDimensionLabel(dimId);
                                      const success = ev.success !== false; // default to true if missing
                                      const resultObj = ev.result || {};
                                      const unwrapped = unwrapSingleKeyObject(resultObj);
                                      
                                      return (
                                        <Accordion key={dimId} defaultExpanded={idx === 0} sx={{ mb: 1.5, border: '1px solid #e2e8f0', borderRadius: '8px !important', '&:before': { display: 'none' }, boxShadow: 'none' }}>
                                          <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            sx={{
                                              backgroundColor: success ? '#f0fdf4' : '#fef2f2',
                                              borderRadius: '8px',
                                              '&.Mui-expanded': {
                                                borderBottomLeftRadius: 0,
                                                borderBottomRightRadius: 0,
                                              }
                                            }}
                                          >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                              <Typography variant="h6" sx={{ fontWeight: 700, color: '#334155', fontSize: '1rem', flex: 1 }}>
                                                {title}
                                              </Typography>
                                              <Chip label={success ? 'Success' : 'Failed'} size="small" sx={{ backgroundColor: success ? '#dcfce7' : '#fee2e2', color: success ? '#166534' : '#991b1b', fontWeight: 600 }} />
                                            </Box>
                                          </AccordionSummary>
                                          <AccordionDetails sx={{ p: 2.25, backgroundColor: '#fafafa' }}>
                                            {/* First, render curated view for common fields */}
                                            {renderEvaluationResult(resultObj)}
                                            {/* Then, ensure we don't miss any nested attributes by deep rendering */}
                                            {Object.entries(unwrapped).map(([k, v]) => (
                                              <Box key={`${dimId}-${k}`} sx={{ mt: 1 }}>
                                                {renderDeepNode(k, v)}
                                              </Box>
                                            ))}
                                          </AccordionDetails>
                                        </Accordion>
                                      );
                                    })}
                                  </Box>
                                );
                              }

                              // Fallback to old generic rendering
                              return (
                                <>
                                  {Object.entries(md || {}).map(([key, value]) => {
                                    if (value === null || value === undefined || value === '') return null;
                                    return (
                                      <Box key={key}>
                                        <Box sx={{ mb: 2, pb: 1, borderBottom: '2px solid #667eea' }}>
                                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#667eea', fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            {formatFieldName(key)}
                                          </Typography>
                                        </Box>
                                        {renderMatchSection(key, value)}
                                      </Box>
                                    );
                                  })}
                                </>
                              );
                            })()}
                          </Box>
                        )}
                      </Box>
                    ) : result.success ? (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        Match analysis data not available
                      </Alert>
                    ) : (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        {result.error || 'Unknown error occurred'}
                      </Alert>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0' }}>
        <Button 
          onClick={handleClose} 
          disabled={loading || validating}
          sx={{ 
            textTransform: 'none',
            fontWeight: 500,
            color: '#64748b',
            '&:hover': {
              backgroundColor: '#f1f5f9',
            }
          }}
        >
          {showResults ? 'Close' : 'Cancel'}
        </Button>
        {!showResults ? (
          <Button
            variant="contained"
            onClick={handleValidate}
            disabled={selectedDimensions.length === 0 || loading || validating}
            startIcon={validating ? <CircularProgress size={20} color="inherit" /> : <ValidateIcon />}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #653a8e 100%)',
                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)',
              },
              '&:disabled': {
                backgroundColor: '#e2e8f0',
                color: '#94a3b8',
                boxShadow: 'none',
              },
            }}
          >
            {validating 
              ? 'Validating...' 
              : `Validate ${selectedResumes.length} Resume${selectedResumes.length !== 1 ? 's' : ''}`
            }
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleFinish}
            startIcon={<CheckIcon />}
            sx={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                boxShadow: '0 6px 16px rgba(16, 185, 129, 0.5)',
              },
            }}
          >
            Finish & Update
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ResumeValidationDialog;

